import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DocumentPicker } from './DocumentPicker.js';

interface QuarantineRow {
  readonly quarantineId: string;
  readonly documentCode: string | null;
  readonly documentName: string | null;
  readonly quarantineReasonCode: string;
  readonly threatDetail: string | null;
  readonly scannerName: string | null;
  readonly status: string;
  readonly quarantinedAt: string;
}

const REASONS = ['MALWARE', 'VIRUS', 'DLP', 'SUSPICIOUS_CONTENT', 'MANUAL', 'OTHER'] as const;

export function DocumentQuarantinePage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<QuarantineRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState('');
  const [reasonCode, setReasonCode] = useState<string>('MALWARE');
  const [threatDetail, setThreatDetail] = useState('');
  const [scannerName, setScannerName] = useState('');

  function load(): void {
    setError(null);
    apiRequest<{ data: QuarantineRow[] }>('/v1/quarantine/active', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  function onQuarantine(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/quarantine/documents/${encodeURIComponent(id)}`, {
      method: 'POST',
      token,
      body: {
        quarantineReasonCode: reasonCode,
        threatDetail: threatDetail.trim() || undefined,
        scannerName: scannerName.trim() || undefined,
      },
    })
      .then(() => {
        setMessage(t('m23.quarantine.msgQuarantined'));
        setDocumentId('');
        setThreatDetail('');
        setScannerName('');
        load();
      })
      .catch((err) => setError((err as Error).message));
  }

  function onResolve(quarantineId: string, status: 'RELEASED' | 'DELETED'): void {
    const reason = window.prompt(
      status === 'RELEASED'
        ? t('m23.quarantine.releasePrompt')
        : t('m23.quarantine.deletePrompt'),
    );
    if (reason === null || reason.trim().length === 0) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/quarantine/${encodeURIComponent(quarantineId)}/release`, {
      method: 'POST',
      token,
      body: { status, resolutionReason: reason },
    })
      .then(() => {
        setMessage(status === 'RELEASED' ? t('m23.quarantine.msgReleased') : t('m23.quarantine.msgDeleted'));
        load();
      })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="document-quarantine">
      <h1>{t('m23.quarantine.title')}</h1>
      <p>
        {t('m23.quarantine.subtitle')}
      </p>
      <form aria-label="quarantine-form" onSubmit={onQuarantine}>
        <DocumentPicker label={t('m23.quarantine.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <label htmlFor="reasonCode">{t('m23.quarantine.reason')}</label>
        <select
          id="reasonCode"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        >
          {REASONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <label htmlFor="threatDetail">{t('m23.quarantine.threatDetail')}</label>
        <input
          id="threatDetail"
          value={threatDetail}
          onChange={(e) => setThreatDetail(e.target.value)}
        />
        <label htmlFor="scannerName">{t('m23.quarantine.scanner')}</label>
        <input
          id="scannerName"
          value={scannerName}
          onChange={(e) => setScannerName(e.target.value)}
        />
        <button type="submit" data-testid="submit-quarantine">{t('m23.quarantine.submit')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows && rows.length === 0 && <p data-testid="quarantine-empty">{t('m23.quarantine.empty')}</p>}
      {rows && rows.length > 0 && (
        <table aria-label="quarantine-table">
          <thead>
            <tr>
              <th>{t('m23.quarantine.colDocument')}</th>
              <th>{t('m23.quarantine.colReason')}</th>
              <th>{t('m23.quarantine.colThreat')}</th>
              <th>{t('m23.quarantine.colScanner')}</th>
              <th>{t('m23.quarantine.colStatus')}</th>
              <th>{t('m23.quarantine.colQuarantinedAt')}</th>
              <th>{t('m23.quarantine.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.quarantineId} data-testid={`quarantine-row-${q.quarantineId}`}>
                <td>{[q.documentCode, q.documentName].filter(Boolean).join(' \u2014 ')}</td>
                <td>{q.quarantineReasonCode}</td>
                <td>{q.threatDetail ?? '—'}</td>
                <td>{q.scannerName ?? '—'}</td>
                <td>{q.status}</td>
                <td>{at(q.quarantinedAt)}</td>
                <td>
                  <button
                    type="button"
                    data-testid={`release-${q.quarantineId}`}
                    onClick={() => onResolve(q.quarantineId, 'RELEASED')}
                  >
                    {t('m23.quarantine.release')}
                  </button>
                  <button
                    type="button"
                    data-testid={`delete-${q.quarantineId}`}
                    onClick={() => onResolve(q.quarantineId, 'DELETED')}
                  >
                    {t('m23.quarantine.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
